import React, { RefObject, useEffect, useRef, useState } from 'react';
import './ClientDetail.scss';
import SideNav from '../../components/shared/sidenav/SideNav';
import { toast, ToastContainer } from 'react-toastify';
import ApiService from '../../services/apiService';
import { toastOptions } from '../../utils/toast';
import URL from '../../urls/Urls';
import { useNavigate, useParams } from 'react-router-dom';
import XetImage from '../../components/shared/xet-image/XetImage';
import NewPocForm from '../../components/oms/NewPocForm/NewPocForm';
import { Tooltip } from '@mui/material';
import XetModal from '../../components/shared/xet-modal/XetModal';
import AddNewCompanyAddress from '../../components/AddNewCompanyAddress/AddNewCompanyAddress';
import PaymentEmail from '../../components/oms/PaymentEmail/PaymentEmail';

interface IPOCDetail {
  id: number;
  name: string;
  designation: string;
  phone_number: string;
  email: string;
}
interface IAddress {
  id: number;
  address_line: string;
  address_type: string;
  city: string;
  country: string;
  pincode: string;
  state: string;
  active: boolean;
  map_link: string;
  gstin: string;
}

export interface ICompany {
  avatar: string | null;
  category: string;
  domain: string | null;
  id: number;
  name: string;
  uuid: string;
}

export interface IAddressPoc {
  id: number;
  poc_name: string;
  contact_number: string;
}

export interface IAddressWithPoc {
  address: IAddress;
  poc?: IAddressPoc;
}

export interface IClientDetail {
  company: ICompany;
  company_address_with_poc: IAddressWithPoc[];
  company_pocs: IPOCDetail[];
}

const ClientDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [paymentEmail, setPaymentEmail] = useState(false);
  const [clientDetail, setClientDetail] = useState<IClientDetail | null>(null);
  const [openNewPocForm, setOpenNewPocForm] = useState(false);
  const [openEditingPocFormID, setOpenEditingPocFormID] = useState(-1);
  const [selectedAddressType, setSelectedAddressType] = useState('BILLING');
  const [billingAddresses, setBillingAddress] = useState<IAddressWithPoc[]>([]);
  const [warehouseAddresses, setWarehouseAddresses] = useState<
    IAddressWithPoc[]
  >([]);
  const [openBillingAddressDialog, setOpenBillingAddressDialog] =
    useState(false);
  const [openWarehouseAddressDialog, setOpenWarehouseAddressDialog] =
    useState(false);
  const [editingIdBilling, setEditingIdBilling] = useState(-1);
  const [editingIdWarehouse, setEditingIdWarehouse] = useState(-1);
  const [companyName, setCompanyName] = useState('');
  const [editingCompanyName, setEditingCompanyName] = useState(false);
  const companyNameRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    getClientDetail();
  }, []);

  useEffect(() => {
    if (editingCompanyName && companyNameRef.current) {
      (companyNameRef.current as HTMLParagraphElement).focus();
      setCaretToEnd(companyNameRef.current);
    }
  }, [editingCompanyName]);

  const getClientDetail = async () => {
    try {
      const { data } = await ApiService().client.get(
        URL.COMPANY.GET_COMPANY_DETAIL(Number(id))
      );
      if (data) {
        setClientDetail(data);
        const addresses = data.company_address_with_poc || [];

        const billing = addresses.filter(
          (addr: IAddressWithPoc) => addr.address.address_type === 'BILLING'
        );
        const warehouse = addresses.filter(
          (addr: IAddressWithPoc) => addr.address.address_type === 'WAREHOUSE'
        );

        setBillingAddress(billing);
        setWarehouseAddresses(warehouse);
        setCompanyName(data.company.name);
      }
    } catch (error) {
      console.error(error);
      toast.error('Error Getting Clients Details ', toastOptions);
    }
  };

  const handleAddingNewPoc = async (pocDetail: IPOCDetail) => {
    if (openNewPocForm) {
      try {
        const { data } = await ApiService().client.post(
          URL.COMPANY.ADD_NEW_POC(Number(id)),
          {
            name: pocDetail.name,
            email: pocDetail.email,
            designation: pocDetail.designation,
            phone_number: pocDetail.phone_number,
          }
        );
        setClientDetail((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            company_pocs: [...prev.company_pocs, data],
          };
        });
        setOpenNewPocForm(false);
      } catch (error) {
        console.error('Error Adding New POC ', error);
      }
    } else {
      try {
        const { data } = await ApiService().client.put(
          URL.COMPANY.UPDATE_POC(openEditingPocFormID),
          {
            name: pocDetail.name,
            email: pocDetail.email,
            designation: pocDetail.designation,
            phone_number: pocDetail.phone_number,
          }
        );

        setClientDetail((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            company_pocs: prev.company_pocs.map((poc) =>
              poc.id === openEditingPocFormID ? data : poc
            ),
          };
        });
        setOpenEditingPocFormID(-1);
      } catch (error) {
        console.error('Error Updating POC ', error);
      }
    }
  };

  const handleAddressDialogOpen = () => {
    if (selectedAddressType === 'BILLING') {
      setOpenBillingAddressDialog(true);
    } else {
      setOpenWarehouseAddressDialog(true);
    }
  };

  const handleAddressTypeChange = () => {
    if (selectedAddressType === 'BILLING') {
      setSelectedAddressType('WAREHOUSE');
    } else {
      setSelectedAddressType('BILLING');
    }
  };

  const handleAddressUpdate = async (
    address_id: number,
    address_type: string,
    params: any
  ) => {
    try {
      const { data } = await ApiService().client.put(
        URL.USERS.UPDATE_ADDRESS(address_id),
        params
      );

      const updatedAddressWithPoc: IAddressWithPoc = {
        address: {
          id: data.data.address.id,
          address_line: data.data.address.address_line,
          address_type: data.data.address.address_type,
          city: data.data.address.city,
          country: data.data.address.country,
          pincode: data.data.address.pincode,
          state: data.data.address.state,
          active: data.data.address.active,
          map_link: data.data.address.map_link,
          gstin: data.data.address.gstin,
        },
        poc: {
          id: data.data.address_poc.id,
          poc_name: data.data.address_poc.poc_name,
          contact_number: data.data.address_poc.contact_number,
        },
      };

      setClientDetail((prev) => {
        if (!prev) return prev;

        // Update main list
        const updatedCompanyAddresses = [...prev.company_address_with_poc];
        const index = updatedCompanyAddresses.findIndex(
          (item) => item.address.id === address_id
        );
        if (index !== -1) {
          updatedCompanyAddresses[index] = updatedAddressWithPoc;
        }

        // Update filtered billing/warehouse lists
        const billing = updatedCompanyAddresses.filter(
          (addr) => addr.address.address_type === 'BILLING'
        );
        const warehouse = updatedCompanyAddresses.filter(
          (addr) => addr.address.address_type === 'WAREHOUSE'
        );

        setBillingAddress(billing);
        setWarehouseAddresses(warehouse);

        return {
          ...prev,
          company_address_with_poc: updatedCompanyAddresses,
        };
      });

      setEditingIdBilling(-1);
      setEditingIdWarehouse(-1);
    } catch (error) {
      console.error('Error Updating Address', error);
    }
  };

  const handleNewAddress = async (address_data: any) => {
    try {
      const { data } = await ApiService().client.post(
        URL.USERS.NEW_CLIENT_ADDRESS,
        address_data
      );

      const newAddressEntry: IAddressWithPoc = {
        address: data.data.address,
        poc: data.data.address_poc,
      };

      const type = newAddressEntry.address.address_type.toUpperCase();

      if (type === 'BILLING') {
        setBillingAddress((prev) => [...prev, newAddressEntry]);
      } else if (type === 'WAREHOUSE') {
        setWarehouseAddresses((prev) => [...prev, newAddressEntry]);
      } else {
        console.warn('Unknown address type:', type);
      }
    } catch (error) {
      console.error('Error adding address:', error);
    }
  };

  const handleUpdateCompanyName = async () => {
    try {
      if (clientDetail?.company.name === companyName) {
        setEditingCompanyName(false);
        return;
      }
      const { data } = await ApiService().client.put(
        URL.COMPANY.UPDATE_COMPANY_DETAIL(Number(id)),
        {
          name: companyName,
        }
      );
      setEditingCompanyName(false);
    } catch (error) {
      console.error('Error Updating Company Name', error);
    }
  };

  const handleBlur = (
    ref: RefObject<HTMLElement>,
    setter: React.Dispatch<React.SetStateAction<string>>
  ) => {
    setter(ref.current?.innerText || '');
  };

  const setCaretToEnd = (element: HTMLElement) => {
    const range = document.createRange();
    const selection = window.getSelection();
    range.selectNodeContents(element);
    range.collapse(false);
    selection?.removeAllRanges();
    selection?.addRange(range);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLParagraphElement>,
    ref: RefObject<HTMLParagraphElement>
  ) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
    if (
      event.key === 'Backspace' &&
      ref.current?.innerText === '' &&
      companyNameRef.current
    ) {
      (companyNameRef.current as HTMLParagraphElement).focus();
      setCaretToEnd(companyNameRef.current);
      event.preventDefault();
    }
  };

  const showPaymentEmail = () => {
    setPaymentEmail(true);
  };

  const hidePaymentEmail = () => {
    setPaymentEmail(false);
  };

  return (
    <div className="flex-row full-width full-height">
      <SideNav></SideNav>

      <div className="client-detail-main-page full-width flex-column ">
        <div className="upper-bar">
          <p className="xetgo-font-tag bold px-16 py-12">
            <span
              className="sub-title cursor-pointer"
              onClick={() => navigate('/clients')}
            >
              Clients{' '}
            </span>
            <span className="xetgo-font-tag">/ {companyName}</span>
          </p>
        </div>
        {paymentEmail ? (
          <PaymentEmail hidePaymentEmail={hidePaymentEmail} />
        ) : (
          <div className="flex-column p-16 flex-1 border-box">
            {clientDetail && (
              <div className="client-detail-page-inner-main-container flex-column gap-8">
                <div className="flex-column gap-2">
                  <div className="company-detail-container flex-column gap-16 p-16">
                    <div className="flex-row align-items-cennter justify-content-space-between">
                      <p className="xetgo-font-tag">
                        {clientDetail.company.uuid}
                      </p>
                      <div className="flex-row align-items-center gap-12">
                        {!editingCompanyName ? (
                          <img
                            onClick={() => setEditingCompanyName(true)}
                            className="cursor-pointer"
                            height={16}
                            width={16}
                            src="https://d2k6zobmg5lufr.cloudfront.net/assets%2F20250313064125-pen-square+%281%29.svg"
                            alt="edit-icon"
                          />
                        ) : (
                          <div
                            onClick={() => handleUpdateCompanyName()}
                            className="save xetgo-font-tag bold flex-row align-items-end cursor-pointer"
                          >
                            Save
                          </div>
                        )}
                        <div
                          className="email-btn flex-row gap-8 align-items-center"
                          onClick={showPaymentEmail}
                        >
                          <img
                            src="https://d2k6zobmg5lufr.cloudfront.net/assets%2F20251106114042-Vector+%283%29.svg"
                            alt="mail"
                          />
                          <p>Send Email</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex-row gap-12 align-items-center xetgo-font-tag">
                      <XetImage
                        className="avatar"
                        src={
                          clientDetail.company.avatar
                            ? clientDetail.company.avatar
                            : ''
                        }
                        alt={clientDetail.company.name}
                        width={22}
                        height={22}
                        key={clientDetail.company.id}
                      />
                      {/* <p style={{ color: '#6B7280' }}>
                      {clientDetail.company.name}
                    </p> */}
                      <p
                        ref={companyNameRef}
                        contentEditable={editingCompanyName}
                        suppressContentEditableWarning={true}
                        onBlur={() =>
                          handleBlur(companyNameRef, setCompanyName)
                        }
                        onKeyDown={(e) => handleKeyDown(e, companyNameRef)}
                        className="company-name"
                      >
                        {companyName}
                      </p>
                    </div>
                  </div>
                  <div className="company-poc-detail-main-container ">
                    <div className="company-poc-heading py-12 px-16 flex-row align-items-center justify-content-space-between xetgo-font-tag">
                      <p>POC</p>
                      <div
                        className="new-poc-btn flex-row align-items-center cursor-pointer"
                        onClick={() => setOpenNewPocForm(true)}
                      >
                        <img
                          src="https://d2k6zobmg5lufr.cloudfront.net/assets%2F20231007090447-white_add.svg"
                          alt="new-task"
                          width={15}
                          height={16}
                        />
                        <p className="xetgo-font-tag m-0 title">Add POC</p>
                      </div>
                    </div>
                    {clientDetail.company_pocs.length > 0 ? (
                      <div className="all-pocs-container">
                        {clientDetail.company_pocs.map((poc, index) => (
                          <div
                            key={poc.id}
                            className="poc-container flex-row gap-16 px-16"
                          >
                            <div className="flex-column gap-8 py-16 flex-1">
                              <p
                                style={{ color: '#333333' }}
                                className="xetgo-font-tag"
                              >
                                POC Name
                              </p>
                              <p
                                style={{ color: '#8D8D8D' }}
                                className="xetgo-font-tag"
                              >
                                {poc.name}
                              </p>
                            </div>
                            <div className="flex-column gap-8 py-16 flex-1">
                              <p
                                style={{ color: '#333333' }}
                                className="xetgo-font-tag"
                              >
                                Email
                              </p>
                              <p
                                style={{ color: '#8D8D8D' }}
                                className="xetgo-font-tag"
                              >
                                {poc.email}
                              </p>
                            </div>
                            <div className="flex-column gap-8 py-16 flex-1">
                              <p
                                style={{ color: '#333333' }}
                                className="xetgo-font-tag"
                              >
                                Designation
                              </p>
                              <p
                                style={{ color: '#8D8D8D' }}
                                className="xetgo-font-tag"
                              >
                                {poc.designation}
                              </p>
                            </div>
                            <div className="flex-column gap-8 py-16 flex-1">
                              <p
                                style={{ color: '#333333' }}
                                className="xetgo-font-tag"
                              >
                                Mobile No.
                              </p>
                              <p
                                style={{ color: '#8D8D8D' }}
                                className="xetgo-font-tag"
                              >
                                {poc.phone_number}
                              </p>
                            </div>
                            <img
                              onClick={() => setOpenEditingPocFormID(poc.id)}
                              className="cursor-pointer py-16"
                              height={16}
                              width={16}
                              src="https://d2k6zobmg5lufr.cloudfront.net/assets%2F20250313064125-pen-square+%281%29.svg"
                              alt="edit-icon"
                            />
                            {openEditingPocFormID == poc.id && (
                              <div className="new-user-form-container position-relative">
                                <NewPocForm
                                  onCancel={() => setOpenEditingPocFormID(-1)}
                                  onAddNewPoc={handleAddingNewPoc}
                                  editing={true}
                                  pocDetails={{
                                    poc_name: poc.name,
                                    designation: poc.designation,
                                    phone_number: poc.phone_number,
                                    email: poc.email,
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="empty-pocs-container flex-row gap-16 px-16">
                        <div className="flex-column gap-8 py-16 flex-1">
                          <p
                            style={{ color: '#333333' }}
                            className="xetgo-font-tag"
                          >
                            POC Name
                          </p>
                          <p
                            style={{ color: '#8D8D8D' }}
                            className="xetgo-font-tag"
                          >
                            -
                          </p>
                        </div>
                        <div className="flex-column gap-8 py-16 flex-1">
                          <p
                            style={{ color: '#333333' }}
                            className="xetgo-font-tag"
                          >
                            Email
                          </p>
                          <p
                            style={{ color: '#8D8D8D' }}
                            className="xetgo-font-tag"
                          >
                            -
                          </p>
                        </div>
                        <div className="flex-column gap-8 py-16 flex-1">
                          <p
                            style={{ color: '#333333' }}
                            className="xetgo-font-tag"
                          >
                            Designation
                          </p>
                          <p
                            style={{ color: '#8D8D8D' }}
                            className="xetgo-font-tag"
                          >
                            -
                          </p>
                        </div>
                        <div className="flex-column gap-8 py-16 flex-1">
                          <p
                            style={{ color: '#333333' }}
                            className="xetgo-font-tag"
                          >
                            Mobile No.
                          </p>
                          <p
                            style={{ color: '#8D8D8D' }}
                            className="xetgo-font-tag"
                          >
                            -
                          </p>
                        </div>
                      </div>
                    )}
                    {openNewPocForm && (
                      <div className="new-user-form-container position-relative">
                        <NewPocForm
                          onCancel={() => setOpenNewPocForm(false)}
                          onAddNewPoc={handleAddingNewPoc}
                          editing={false}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div
                  style={{ width: '100%' }}
                  className="company-profile-details-main-container-addresses flex-column flex-1"
                >
                  <div
                    style={{ borderBottom: '1px solid rgb(238, 238, 238)' }}
                    className="flex-row justify-content-space-between border-box py-12 px-21 company-detail-padded align-items-center"
                  >
                    <p className="xetgo-font-button bold"> Address</p>
                    <div className="company-profile-address-right-box flex-row gap-12 align-items-center">
                      <Tooltip
                        title={` Add ${selectedAddressType.toLocaleLowerCase()} address`}
                      >
                        <img
                          onClick={handleAddressDialogOpen}
                          className="cursor-pointer"
                          src="https://d2k6zobmg5lufr.cloudfront.net/assets%2F20240408112655-package-plus.svg"
                          alt="add-icon"
                        />
                      </Tooltip>

                      <div
                        onClick={handleAddressTypeChange}
                        className="toggle-address-box flex-row  xetgo-font-mini align-items-center bolder cursor-pointer p-2"
                      >
                        <p
                          className={`${
                            selectedAddressType === 'BILLING' &&
                            'active-address-company'
                          }  py-5 px-8`}
                        >
                          Billing
                        </p>
                        <p
                          className={`${
                            selectedAddressType === 'WAREHOUSE' &&
                            'active-address-company'
                          }  py-5 px-8`}
                        >
                          Warehouse
                        </p>
                      </div>
                    </div>
                  </div>

                  {selectedAddressType === 'BILLING' &&
                    billingAddresses.length > 0 && (
                      <div className="flex-column all-addresses-container p-12 gap-8">
                        {billingAddresses.map((address, index) => {
                          return (
                            <div
                              key={index}
                              className="company-profile-address-container flex-row justify-content-space-between xetgo-font-tag p-12 gap-24"
                            >
                              <div className="flex-row flex-1">
                                <div className="flex-column gap-4 address-poc-box flex-1">
                                  <div className="bold city-name flex-row gap-4 align-items-center justify-content-space-between">
                                    <p
                                      style={{ color: '#5151EC' }}
                                      className="flex-row align-items-center gap-8"
                                    >
                                      <span>
                                        {address.address.city},{' '}
                                        {address.address.state},
                                        {address.address.pincode},(
                                        {address.address.country})
                                      </span>
                                      {address.address.map_link && (
                                        <span className="flex-row align-items-center">
                                          <a
                                            href={address.address.map_link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex-row align-items-center"
                                          >
                                            <img
                                              className="cursor-pointer"
                                              height={16}
                                              width={16}
                                              src="https://d2k6zobmg5lufr.cloudfront.net/assets%2F20241004110343-location-purple.svg"
                                              alt="location-icon"
                                            />
                                          </a>
                                        </span>
                                      )}
                                    </p>
                                    <div className="flex-row gap-8 align-items-center">
                                      {address.address.gstin && (
                                        <p className="bold city-name flex-row gap-4">
                                          <span style={{ color: '##5B5B5B' }}>
                                            GSTIN:
                                          </span>

                                          <span style={{ color: '#8D8D8D' }}>
                                            {address.address?.gstin}
                                          </span>
                                        </p>
                                      )}
                                      {address.address.active ? (
                                        <p
                                          className={`address-status active-address p-4`}
                                        ></p>
                                      ) : (
                                        <p className={`address-status p-4`}></p>
                                      )}
                                    </div>
                                  </div>
                                  <p className="bold city-name flex-row gap-4">
                                    <span style={{ color: '##5B5B5B' }}>
                                      {address.poc?.poc_name}
                                    </span>

                                    <span style={{ color: '#8D8D8D' }}>
                                      {address.poc?.contact_number}
                                    </span>
                                  </p>

                                  {address.address.id === editingIdBilling && (
                                    <XetModal
                                      backdropClose={true}
                                      open={
                                        address.address.id === editingIdBilling
                                      }
                                      handleClose={() =>
                                        setEditingIdBilling(-1)
                                      }
                                    >
                                      <AddNewCompanyAddress
                                        addressType="BILLING"
                                        companyId={Number(id)}
                                        handleClose={() =>
                                          setEditingIdBilling(-1)
                                        }
                                        update={true}
                                        editingData={{
                                          id: address.address.id,
                                          address_line:
                                            address.address.address_line,
                                          address_type:
                                            address.address.address_type,
                                          city: address.address.city,
                                          country: address.address.country,
                                          pincode: address.address.pincode,
                                          state: address.address.state,
                                          poc_id: address.poc?.id,
                                          poc_name: address.poc?.poc_name,
                                          poc_number:
                                            address.poc?.contact_number,
                                          active: address.address.active,
                                          map_link: address.address.map_link,
                                          gstin: address.address.gstin,
                                        }}
                                        handleAddressUpdate={
                                          handleAddressUpdate
                                        }
                                        handleNewAddressAdd={handleNewAddress}
                                      />
                                    </XetModal>
                                  )}
                                  <div
                                    style={{ color: '#3333338F' }}
                                    className="address-line"
                                  >
                                    <p>{`${address.address.address_line}, ${address.address.state}`}</p>
                                    <p>{address.address.pincode}</p>
                                  </div>
                                </div>
                              </div>
                              <img
                                onClick={() =>
                                  setEditingIdBilling(address.address.id)
                                }
                                className="cursor-pointer"
                                height={16}
                                width={16}
                                src="https://d2k6zobmg5lufr.cloudfront.net/assets%2F20250108082347-pen-square.svg"
                                alt="edit-icon"
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}

                  {selectedAddressType === 'WAREHOUSE' &&
                    warehouseAddresses.length > 0 && (
                      <div className="flex-column all-addresses-container p-12 gap-8">
                        {warehouseAddresses.length > 0 &&
                          warehouseAddresses.map((address, index) => {
                            return (
                              <div
                                key={index}
                                className="company-profile-address-container flex-row justify-content-space-between xetgo-font-tag p-12 gap-24"
                              >
                                <div className="flex-row flex-1">
                                  <div className="flex-column gap-4 flex-1 ">
                                    <div className="bold city-name flex-row gap-4 align-items-center justify-content-space-between">
                                      <p
                                        style={{ color: '#5151EC' }}
                                        className="flex-row align-items-center gap-8"
                                      >
                                        <span>
                                          {address.address.city},{' '}
                                          {address.address.state},
                                          {address.address.pincode},(
                                          {address.address.country})
                                        </span>
                                        {address.address.map_link && (
                                          <span className="flex-row align-items-center">
                                            <a
                                              href={address.address.map_link}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="flex-row align-items-center"
                                            >
                                              <img
                                                className="cursor-pointer"
                                                height={16}
                                                width={16}
                                                src="https://d2k6zobmg5lufr.cloudfront.net/assets%2F20241004110343-location-purple.svg"
                                                alt="location-icon"
                                              />
                                            </a>
                                          </span>
                                        )}
                                      </p>

                                      <div className="flex-row gap-8 align-items-center">
                                        {address.address.gstin && (
                                          <p className="bold city-name flex-row gap-4">
                                            <span style={{ color: '##5B5B5B' }}>
                                              GSTIN:
                                            </span>

                                            <span style={{ color: '#8D8D8D' }}>
                                              {address.address?.gstin}
                                            </span>
                                          </p>
                                        )}
                                        {address.address.active ? (
                                          <p
                                            className={`address-status active-address p-4`}
                                          ></p>
                                        ) : (
                                          <p
                                            className={`address-status p-4`}
                                          ></p>
                                        )}
                                      </div>
                                    </div>
                                    <p className="bold city-name flex-row gap-4">
                                      <span style={{ color: '##5B5B5B' }}>
                                        {address.poc?.poc_name}
                                      </span>

                                      <span style={{ color: '#8D8D8D' }}>
                                        {address.poc?.contact_number}
                                      </span>
                                    </p>
                                    {/* <p className="bold city-name flex-row gap-4">
                                    <span style={{ color: '##5B5B5B' }}>
                                      GSTIN:
                                    </span>

                                    <span style={{ color: '#8D8D8D' }}>
                                      {address.address?.gstin}
                                    </span>
                                  </p> */}
                                    {address.address.id ===
                                      editingIdWarehouse && (
                                      <XetModal
                                        backdropClose={true}
                                        open={
                                          address.address.id ===
                                          editingIdWarehouse
                                        }
                                        handleClose={() =>
                                          setEditingIdWarehouse(-1)
                                        }
                                      >
                                        <AddNewCompanyAddress
                                          addressType="WAREHOUSE"
                                          companyId={Number(id)}
                                          handleClose={() =>
                                            setEditingIdWarehouse(-1)
                                          }
                                          update={true}
                                          editingData={{
                                            id: address.address.id,
                                            address_line:
                                              address.address.address_line,
                                            address_type:
                                              address.address.address_type,
                                            city: address.address.city,
                                            country: address.address.country,
                                            pincode: address.address.pincode,
                                            state: address.address.state,
                                            poc_id: address.poc?.id,
                                            poc_name: address.poc?.poc_name,
                                            poc_number:
                                              address.poc?.contact_number,
                                            active: address.address.active,
                                            gstin: address.address.gstin,
                                            map_link: address.address.map_link,
                                          }}
                                          handleAddressUpdate={
                                            handleAddressUpdate
                                          }
                                          handleNewAddressAdd={handleNewAddress}
                                        />
                                      </XetModal>
                                    )}
                                    <div className="address-line">
                                      <p
                                        style={{ color: '#3333338F' }}
                                      >{`${address.address.address_line}, ${address.address.state}, ${address.address.pincode}`}</p>
                                    </div>
                                  </div>
                                </div>
                                <img
                                  onClick={() =>
                                    setEditingIdWarehouse(address.address.id)
                                  }
                                  className="cursor-pointer"
                                  height={16}
                                  width={16}
                                  src="https://d2k6zobmg5lufr.cloudfront.net/assets%2F20250108082347-pen-square.svg"
                                  alt="edit-icon"
                                />
                              </div>
                            );
                          })}
                      </div>
                    )}

                  {selectedAddressType === 'BILLING' &&
                    billingAddresses.length === 0 && (
                      <div className="company-profile-empty-adrress flex-column full-width  align-items-center gap-2 justify-content-center flex-1 p-16">
                        <img
                          height={24}
                          width={24}
                          src="https://d2k6zobmg5lufr.cloudfront.net/assets%2F20250521052629-building-2.svg"
                          alt="empty-icon"
                        />
                        <p
                          style={{ color: '#B2B2B2' }}
                          className="xetgo-font-tag bold"
                        >
                          No Address found
                        </p>
                      </div>
                    )}
                  {selectedAddressType === 'WAREHOUSE' &&
                    warehouseAddresses.length === 0 && (
                      <div className="company-profile-empty-adrress flex-column full-width  align-items-center gap-2 justify-content-center flex-1 p-16">
                        <img
                          height={24}
                          width={24}
                          src="https://d2k6zobmg5lufr.cloudfront.net/assets%2F20250521052629-building-2.svg"
                          alt="empty-icon"
                        />
                        <p
                          style={{ color: '#B2B2B2' }}
                          className="xetgo-font-tag bold"
                        >
                          No Address found
                        </p>
                      </div>
                    )}
                </div>
                <XetModal
                  backdropClose={true}
                  open={openBillingAddressDialog}
                  handleClose={() => setOpenBillingAddressDialog(false)}
                >
                  <AddNewCompanyAddress
                    addressType="BILLING"
                    companyId={Number(id)}
                    handleClose={() => setOpenBillingAddressDialog(false)}
                    handleAddressUpdate={() => console.log('n')}
                    handleNewAddressAdd={handleNewAddress}
                  />
                </XetModal>
                <XetModal
                  backdropClose={true}
                  open={openWarehouseAddressDialog}
                  handleClose={() => setOpenWarehouseAddressDialog(false)}
                >
                  <AddNewCompanyAddress
                    addressType="WAREHOUSE"
                    companyId={Number(id)}
                    handleClose={() => setOpenWarehouseAddressDialog(false)}
                    handleAddressUpdate={() => console.log('n')}
                    handleNewAddressAdd={handleNewAddress}
                  />
                </XetModal>
              </div>
            )}
          </div>
        )}
      </div>

      <ToastContainer position="top-right" />
    </div>
  );
};

export default ClientDetail;
